
"use client"

import { X } from "lucide-react"
import { useTheme } from "next-themes"
import { Toaster as Sonner, toast, useSonner } from "sonner"

type ToasterProps = React.ComponentProps<typeof Sonner>

const CLEAR_ALL_OFFSET_TOP = 76
const DEFAULT_TOAST_OFFSET = 32
const CLOSE_BUTTON_CLASS =
  "!bg-white !text-slate-700 !border-slate-200 shadow-sm hover:!bg-slate-50 hover:!text-slate-950 hover:!border-slate-300 focus-visible:!ring-2 focus-visible:!ring-emerald-700 focus-visible:!ring-offset-2"

const Toaster = ({ offset, position = "bottom-right", ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()
  const { toasts } = useSonner()
  const showClearAll = toasts.length > 1
  const topPosition = position.startsWith("top")
  const adjustedOffset =
    showClearAll && topPosition && offset === undefined
      ? { top: CLEAR_ALL_OFFSET_TOP, right: DEFAULT_TOAST_OFFSET }
      : offset

  return (
    <>
      {showClearAll && (
        <button
          type="button"
          aria-label="Clear all notifications"
          className="fixed right-8 top-8 z-[1000000000] inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white/95 px-2.5 text-xs font-semibold text-slate-700 shadow-lg backdrop-blur transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-2 max-[600px]:right-4 max-[600px]:top-4"
          onClick={() => toast.dismiss()}
        >
          <X aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.5} />
          Clear all
        </button>
      )}
      <Sonner
        theme={theme as ToasterProps["theme"]}
        position={position}
        offset={adjustedOffset}
        className="toaster group"
        toastOptions={{
          classNames: {
            toast:
              "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
            description: "group-[.toast]:text-muted-foreground",
            actionButton:
              "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
            cancelButton:
              "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
            closeButton: CLOSE_BUTTON_CLASS,
          },
        }}
        {...props}
      />
    </>
  )
}

export { Toaster }
